import * as React from 'react'

import { IconChecks, IconLoader2 } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { isToday, isYesterday } from 'date-fns'

import type { Notification } from '@/api/v1/platform/notifications'
import {
  useGetNotificationStats,
  useListNotificationsInfinite,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
} from '@/api/v1/platform/notifications'
import { Badge } from '@/components/sui/badge'
import { Button } from '@/components/sui/button'
import { ScrollArea } from '@/components/sui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '@/components/sui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { useComponentTranslation } from '@/hooks/use-component-translation'

import { EmptyState, NotificationItem } from './components'
import { formatBadgeCount } from './format-badge-count'
import locales from './locales'
import {
  NotificationItemSkeleton,
  NotificationsPanelSkeleton,
} from './notifications-panel.skeleton'

type FilterValue = 'unread' | 'all'

function getFilterByMarkedAsRead(filter: FilterValue): boolean | undefined {
  return filter === 'unread' ? false : undefined
}

function getNotificationUrl(notification: Notification): string | null {
  const envelopeId =
    notification.envelopeAction?.envelopeId ??
    notification.signerAction?.envelopeId

  switch (notification.type) {
    case 'InternalInvite':
      return '/settings/profile/pending-invites'
    case 'EnvelopePublished':
    case 'EnvelopeScheduled':
    case 'ScheduledEnvelopePublished':
    case 'EnvelopeViewed':
    case 'EnvelopeApproved':
    case 'EnvelopeRejected':
    case 'EnvelopeCompleted':
    case 'EnvelopeExpired':
    case 'EnvelopeCancelled':
    case 'EnvelopeCancelledBySignerMfaError':
      if (envelopeId) {
        return `/envelopes/${envelopeId}`
      }
      return null
    default:
      return null
  }
}

type GroupKey = 'today' | 'yesterday' | 'older'

function groupNotifications(notifications: Array<Notification>) {
  const groups: Record<GroupKey, Array<Notification>> = {
    today: [],
    yesterday: [],
    older: [],
  }

  for (const notification of notifications) {
    const date = new Date(notification.createdAtUtc)
    if (isToday(date)) {
      groups.today.push(notification)
    } else if (isYesterday(date)) {
      groups.yesterday.push(notification)
    } else {
      groups.older.push(notification)
    }
  }

  return (Object.keys(groups) as Array<GroupKey>)
    .map((key) => ({ key, items: groups[key] }))
    .filter((group) => group.items.length > 0)
}

interface NotificationsPanelProps {
  /** Called right before navigating away so the host (popover/drawer) closes. */
  onNavigate?: () => void
}

export function NotificationsPanel({ onNavigate }: NotificationsPanelProps) {
  const { t } = useComponentTranslation(locales)
  const navigate = useNavigate()
  const [filter, setFilter] = React.useState<FilterValue>('unread')
  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  const { data: stats } = useGetNotificationStats()
  const {
    data: notificationsData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useListNotificationsInfinite({
    filterByMarkedAsRead: getFilterByMarkedAsRead(filter),
    pageSize: 8,
  })

  const markAsReadMutation = useMarkNotificationAsRead()
  const markAllAsReadMutation = useMarkAllNotificationsAsRead()

  // Infinite scroll with IntersectionObserver
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const notifications = React.useMemo(
    () => notificationsData?.pages.flatMap((page) => page.items ?? []) ?? [],
    [notificationsData],
  )

  const groups = React.useMemo(
    () => groupNotifications(notifications),
    [notifications],
  )

  const unreadCount = stats?.nonRead ?? 0

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.markedAsRead) {
      markAsReadMutation.mutate(notification.id)
    }

    const url = getNotificationUrl(notification)
    if (url) {
      onNavigate?.()
      navigate({ to: url })
    }
  }

  const handleMarkAsRead = (notification: Notification) => {
    markAsReadMutation.mutate(notification.id)
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
        <p className="text-sm font-semibold">{t('title')}</p>
        {/* Mark-all sits left of the tabs so the tabs keep their right anchor
            when the button disappears (no unread left). */}
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground size-7"
                    aria-label={t('markAllAsRead')}
                    onClick={() => markAllAsReadMutation.mutate({})}
                    disabled={markAllAsReadMutation.isPending}
                  />
                }
              >
                {markAllAsReadMutation.isPending ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconChecks className="size-4" />
                )}
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('markAllAsRead')}
              </TooltipContent>
            </Tooltip>
          )}
          <Tabs
            value={filter}
            onValueChange={(value) => setFilter(value as FilterValue)}
            className="gap-0"
          >
            <TabsList className="h-7">
              <TabsTrigger value="unread" className="gap-1.5 px-2.5 text-xs">
                {t('tabs.unread')}
                {unreadCount > 0 && (
                  <Badge variant="neutral" size="count-xs">
                    {formatBadgeCount(unreadCount)}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="all" className="px-2.5 text-xs">
                {t('tabs.all')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Fixed height so the popover doesn't resize between the skeleton,
          data and empty states. */}
      <ScrollArea className="h-105 min-h-0">
        {isLoading ? (
          <NotificationsPanelSkeleton rowCount={6} />
        ) : notifications.length === 0 ? (
          <div className="flex h-105 items-center justify-center">
            <EmptyState tab={filter} />
          </div>
        ) : (
          <div className="pb-2">
            {groups.map((group) => (
              <React.Fragment key={group.key}>
                <p className="text-muted-foreground px-4 pt-2.5 pb-1 text-[11px] font-semibold tracking-wide uppercase">
                  {t(`groups.${group.key}`)}
                </p>
                {group.items.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={handleNotificationClick}
                    onMarkAsRead={handleMarkAsRead}
                  />
                ))}
              </React.Fragment>
            ))}

            <div ref={loadMoreRef}>
              {isFetchingNextPage && <NotificationItemSkeleton />}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
